/* eslint-disable import/order */
import { createStore, applyMiddleware, compose, combineReducers } from 'redux';
// import { logger } from 'redux-logger';
import createSagaMiddleware from 'redux-saga';
import { all, fork } from 'redux-saga/effects';

// Page Frame
import applyTemplateReducers from './applyTemplate/reducers';
import applyTemplateSagas from './applyTemplate/sagas';
import type { IApplyTemplateState } from './applyTemplate/types';
import { dtmSagas, dtmReducers, type IDTMState } from './dtm';
import envelopeDocumentsReducers from './envelopeDocuments/reducers';
import envelopeDocumentsSagas from './envelopeDocuments/sagas';
import type { IEnvelopeDocumentsState } from './envelopeDocuments/types';
import envelopeManagementReducers from './envelopeManagement/reducers';
import envelopeManagementSagas from './envelopeManagement/sagas';
import pageFrameReducers from './pageFrame/reducers';
import pageFrameSagas from './pageFrame/sagas';
import type { IPageFrameState } from './pageFrame/types';

// Sender Build
import { senderBuildReducer } from './senderBuild/reducers';
import senderBuildSagas from './senderBuild/sagas';
import type { ISenderBuildState } from './senderBuild/types';

// Template Management
import templateManagementReducers from './templateManagement/reducers';
import templateManagementSagas from './templateManagement/sagas';
import type { ITemplateManagementState } from './templateManagement/types';

// Template Edit
import templateCreateSagas from './templateCreate/sagas';
import templateCreateReducers from './templateCreate/reducers';
import type { ITemplateCreateState } from './templateCreate/template-types';

// Apply Template Edit

// Template Tagging
import templateSharingReducers from './templateSharing/reducer';
import templateSharingSagas from './templateSharing/saga';
import type { ITemplateSharingState } from './templateSharing/types';
import templateTaggingReducers from './templateTagging/reducers';
import templateTaggingSagas from './templateTagging/sagas';
import type { ITemplateTaggingState } from './templateTagging/types';

// Template Sharing

// Envelope Management
import type { IEnvelopeManagementState } from './envelopeManagement/types';

// Envelope Documents

// Envelope History
import envelopeHistorySagas from './envelopeHistory/sagas';
import envelopeHistoryReducers from './envelopeHistory/reducers';
import type { IEnvelopeHistoryState } from './envelopeHistory/types';

// DTM

function* rootSaga() {
  yield all([
    fork(pageFrameSagas),
    fork(senderBuildSagas),
    fork(templateManagementSagas),
    fork(templateCreateSagas),
    fork(applyTemplateSagas),
    fork(templateTaggingSagas),
    fork(envelopeManagementSagas),
    fork(envelopeDocumentsSagas),
    fork(envelopeHistorySagas),
    fork(templateSharingSagas),
    ...dtmSagas,
  ]);
}

const rootReducer = combineReducers({
  pageFrame: pageFrameReducers,
  senderBuild: senderBuildReducer,
  templateManagement: templateManagementReducers,
  templateCreate: templateCreateReducers,
  templateSharing: templateSharingReducers,
  applyTemplate: applyTemplateReducers,
  templateTagging: templateTaggingReducers,
  envelopeManagement: envelopeManagementReducers,
  envelopeDocuments: envelopeDocumentsReducers,
  envelopeHistory: envelopeHistoryReducers,
  dtm: dtmReducers,
});

export interface IRootState {
  pageFrame: IPageFrameState;
  senderBuild: ISenderBuildState;
  templateManagement: ITemplateManagementState;
  templateCreate: ITemplateCreateState;
  templateSharing: ITemplateSharingState;
  applyTemplate: IApplyTemplateState;
  templateTagging: ITemplateTaggingState;
  envelopeManagement: IEnvelopeManagementState;
  envelopeDocuments: IEnvelopeDocumentsState;
  envelopeHistory: IEnvelopeHistoryState;
  dtm: IDTMState;
}

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default function configureStore(initialState: Partial<IRootState> = {}) {
  const sagaMiddleware = createSagaMiddleware();
  const middleware: any[] = [sagaMiddleware];
  // if (process.env.NODE_ENV === 'development') {
  //   middleware.push(logger);
  // }
  const enhancers = [applyMiddleware(...middleware)];

  // If Redux DevTools Extension is installed use it, otherwise use Redux compose
  const composeEnhancers =
    process.env.NODE_ENV !== 'test' &&
    typeof window === 'object' &&
    (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      : compose;

  const store = createStore(rootReducer, initialState, composeEnhancers(...enhancers));
  sagaMiddleware.run(rootSaga);
  return store;
}
